import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 22 — Team/Corporate "Our Work" (shared-only) / "My
 * Work" (this member's own private-only) query hooks. Mirrors
 * practiceQueries.js's `request()` error contract exactly.
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });
const jsonPut = (path, payload) => request(path, { method: "PUT", body: JSON.stringify(payload ?? {}) });

export function useOurWork(workspaceId) {
  return useQuery({
    queryKey: ["our-work", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/our-work`),
  });
}

export function useMyWork(workspaceId) {
  return useQuery({
    queryKey: ["my-work", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/my-work`),
  });
}

export function useMyReflections(workspaceId) {
  return useQuery({
    queryKey: ["my-reflections", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/reflections/mine`),
  });
}

export function useShareReflection(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rflId, choice }) =>
      jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/reflections/${rflId}/share`, { audience_selection: { choice } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-work", workspaceId] });
      qc.invalidateQueries({ queryKey: ["our-work", workspaceId] });
    },
  });
}

export function useSetSharedPresentation(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled) => jsonPut(`/api/rehearsal-room/workspaces/${workspaceId}/shared-presentation`, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-record", "overview", workspaceId] });
      qc.invalidateQueries({ queryKey: ["team-record", "overview", workspaceId] });
    },
  });
}
